import {
  Accordion,
  colors,
  Typography,
} from '@flash-tecnologia/hros-web-ui-v2';
import { useSelectedCompany } from '@flash-tecnologia/hros-web-utility';
import { useBffFetch } from 'api/getData';
import { CompanyCompliance } from 'bff/src/domain/model/compliance.model';
import {
  ColumnContainer,
  RowContainer,
  StyledContentContainer,
} from 'components/Containers';
import FileViwer from 'components/FileViwer/FileViwer';
import { CenteredSpinner } from 'components/Spinner/Spinner';
import { withAdminPermission } from 'HOC/withAdminPermission';
import { withFeatureFlagEnabled } from 'HOC/withFeatureFlagEnabled';
import { classificacaoEmpresaSelectOptions } from 'pages/company/selectFieldOptions';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { dispatchToastErrorHandler } from 'utils/dispatchToast';

const StyledAccordion = styled(Accordion)`
  &.MuiPaper-root {
    &::before {
      background-color: transparent;
    }
  }
  color: ${colors.neutral[50]};
`;

type CompanyComplianceState = Omit<CompanyCompliance, 'documents'> & {
  SOCIAL_CONTRACT?: boolean;
  LEGAL_REPRESENTANT_FRONT?: boolean;
  LEGAL_REPRESENTANT_BACK?: boolean;
  POWER_ATTORNEY?: boolean;
  ADDRESS?: boolean;
  RENTAL_CONTRACT?: boolean;
};

const labelBodyColorStyle = { color: colors.neutral[20] };

const CompanyRegulatoryInformation: FC = () => {
  const { selectedCompany } = useSelectedCompany();
  const [companyCompliance, setCompanyCompliance] =
    useState<CompanyComplianceState>();

  const { loading, fetchBff } = useBffFetch<undefined, CompanyCompliance>({
    path: `/v1/companies/${selectedCompany.id}`,
    onSuccess: async (data) => {
      const { documents, ...otherProps } = data;
      const comapnyComplainceState: CompanyComplianceState = {
        ...otherProps,
      };

      for (const doc of documents) {
        if (['PENDING'].includes(doc.status)) {
          comapnyComplainceState[doc.type] = true;
        }
      }
      setCompanyCompliance(comapnyComplainceState);
    },
    onError: (err) => {
      if (![404, 401].includes(err.statusCode)) {
        dispatchToastErrorHandler(err.statusCode);
      }
    },
  });

  useEffect(() => {
    fetchBff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  return (
    <StyledAccordion
      variant="default"
      title="Informações regulatórias"
      description="Consulte os dados e documentação enviados para manter a empresa em conformidade com o BACEN."
      onChange={(_, expanded) => {
        if (expanded && !companyCompliance) {
          fetchBff();
        }
      }}
    >
      {loading ? (
        <CenteredSpinner />
      ) : (
        <StyledContentContainer style={{ padding: '0' }}>
          <div>
            <Typography variant="caption">Capacidade financeira</Typography>
            <Typography variant="body3" style={labelBodyColorStyle}>
              {companyCompliance?.estimatedAnnualRevenue?.description ?? '-'}
            </Typography>
          </div>
          <div>
            <Typography variant="caption">Classificação da empresa</Typography>
            <Typography variant="body3" style={labelBodyColorStyle}>
              {classificacaoEmpresaSelectOptions.find(
                (option) => companyCompliance?.classification === option.value,
              )?.label ?? '-'}
            </Typography>
          </div>
          <div>
            <Typography variant="caption">Contrato Social</Typography>
            {companyCompliance?.SOCIAL_CONTRACT ? (
              <FileViwer fileName="Contrato social" />
            ) : (
              <Typography variant="caption" style={labelBodyColorStyle}>
                -
              </Typography>
            )}
          </div>
          <div>
            <Typography variant="caption">
              Documento do representante legal
            </Typography>
            {companyCompliance?.LEGAL_REPRESENTANT_FRONT && (
              <FileViwer fileName="Representante legal frente" />
            )}
            <div style={{ height: '15px' }} />
            {companyCompliance?.LEGAL_REPRESENTANT_BACK && (
              <FileViwer fileName="Representante legal traz" />
            )}
            {!companyCompliance?.LEGAL_REPRESENTANT_FRONT &&
              !companyCompliance?.LEGAL_REPRESENTANT_BACK && (
                <Typography variant="caption" style={labelBodyColorStyle}>
                  -
                </Typography>
              )}
          </div>
          {companyCompliance?.POWER_ATTORNEY && (
            <div>
              <Typography variant="caption">
                Procuração do representante legal
              </Typography>
              <FileViwer fileName="Procuração do representante legal" />
            </div>
          )}
          <div>
            <Typography variant="caption">Comprovante de endereço</Typography>
            {companyCompliance?.ADDRESS ? (
              <FileViwer fileName="Comprovante de endereço" />
            ) : (
              <Typography variant="caption" style={labelBodyColorStyle}>
                -
              </Typography>
            )}
          </div>
          {companyCompliance?.RENTAL_CONTRACT && (
            <div>
              <Typography variant="caption">Contrato de locação</Typography>
              <FileViwer fileName="Contrato de locação" />
            </div>
          )}
          <div>
            <Typography variant="caption">
              Informações de proprietários, sócios ou diretores
            </Typography>
            <StyledContentContainer
              style={{ padding: '0', gap: '35px', marginTop: '10px' }}
            >
              {companyCompliance?.shareholders?.length ? (
                companyCompliance.shareholders.map((shareholder, i) => {
                  return (
                    <StyledContentContainer
                      key={i}
                      style={{ padding: '0', gap: '15px', marginTop: '10px' }}
                    >
                      {shareholder.isPF !== false ? (
                        <>
                          <RowContainer style={{ gap: '25px' }}>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">Nome</Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.name || '-'}
                              </Typography>
                            </ColumnContainer>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">CPF</Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.document || '-'}
                              </Typography>
                            </ColumnContainer>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">
                                Participação (%)
                              </Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.participation || '-'}
                              </Typography>
                            </ColumnContainer>
                          </RowContainer>

                          <RowContainer style={{ gap: '25px' }}>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">
                                Data de nascimento
                              </Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.birthDate
                                  ? new Date(
                                      shareholder.birthDate,
                                    ).toLocaleDateString('pt-BR', {
                                      timeZone: 'UTC',
                                    }) // Adicionado timeZone para evitar erro de dia
                                  : '-'}
                              </Typography>
                            </ColumnContainer>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">Cargo</Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.position || '-'}
                              </Typography>
                            </ColumnContainer>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">
                                Pessoa Exposta Politicamente
                              </Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.isPEP ? 'Sim' : 'Não'}
                              </Typography>
                            </ColumnContainer>
                          </RowContainer>

                          <RowContainer>
                            <ColumnContainer>
                              <Typography variant="caption">
                                Endereço
                              </Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.address || '-'}
                              </Typography>
                            </ColumnContainer>
                          </RowContainer>
                        </>
                      ) : (
                        <>
                          <RowContainer style={{ gap: '25px' }}>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">
                                Razão Social
                              </Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.legalName || '-'}
                              </Typography>
                            </ColumnContainer>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">CNPJ</Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.document || '-'}
                              </Typography>
                            </ColumnContainer>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">
                                Participação (%)
                              </Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.participation || '-'}
                              </Typography>
                            </ColumnContainer>
                          </RowContainer>

                          <RowContainer style={{ gap: '25px' }}>
                            <ColumnContainer style={{ width: '33.33%' }}>
                              <Typography variant="caption">País</Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.country || '-'}
                              </Typography>
                            </ColumnContainer>
                            <ColumnContainer style={{ width: '66.66%' }}>
                              <Typography variant="caption">
                                Endereço
                              </Typography>
                              <Typography
                                variant="body3"
                                style={labelBodyColorStyle}
                              >
                                {shareholder.address || '-'}
                              </Typography>
                            </ColumnContainer>
                          </RowContainer>
                        </>
                      )}
                    </StyledContentContainer>
                  );
                })
              ) : (
                <Typography variant="caption" style={labelBodyColorStyle}>
                  -
                </Typography>
              )}
            </StyledContentContainer>
          </div>
        </StyledContentContainer>
      )}
    </StyledAccordion>
  );
};

export default withFeatureFlagEnabled(
  withAdminPermission(CompanyRegulatoryInformation),
  'FLASH_OS_ANTI_FRAUD_COMPANY_REGULATORY_INFORMATION_VIEW',
);
